import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  Sparkles,
  Zap,
  Eye,
  EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { shopService } from '../../services/shopService';
import confetti from 'canvas-confetti';
import { showSuccess, showError } from '../../utils/alert';

const CreateShopOwner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    email: '',
    password: '',
    plan: 'STARTER',
    startDate: '',
    endDate: ''
  });

  const [plans, setPlans] = useState([
    { name: 'STARTER', price: '₹999/mo' },
    { name: 'PREMIUM', price: '₹2499/mo' },
    { name: 'ENTERPRISE', price: '₹4999/mo' },
  ]);

  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await shopService.getGlobalSettings();
      if (settings) {
        setPlans([
          { name: 'STARTER', price: `₹${settings.starterPrice}/mo` },
          { name: 'PREMIUM', price: `₹${settings.premiumPrice}/mo` },
          { name: 'ENTERPRISE', price: `₹${settings.enterprisePrice}/mo` },
        ]);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await shopService.createShop(formData);
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#d4af37', '#fcf6ba', '#b8860b']
      });
      showSuccess('Success', 'Merchant Account Created Successfully!');
      setTimeout(() => navigate('/super-admin/dashboard'), 1000);
    } catch (error) {
      console.error(error);
      showError('Onboarding Failed', error.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfdfd] text-black font-sans overflow-y-auto p-12 relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="luxury-card p-12 md:p-16 relative">
          <button 
            onClick={() => navigate('/super-admin/dashboard')}
            className="absolute top-12 left-12 flex items-center gap-2 text-[#999] hover:text-black transition-all text-[10px] font-black uppercase tracking-widest group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Back to Hub
          </button>

          <header className="mb-12 mt-8">
            <h1 className="text-4xl font-serif text-black mb-6">Merchant Onboarding</h1>
            <p className="text-[#666] text-sm leading-relaxed mb-6">
              Register a new enterprise node into the platform. Once created, the merchant can use these credentials to access their personal dashboard.
            </p>
            <div className="w-full h-[1px] bg-black/[0.05]" />
          </header>

          <form onSubmit={handleSubmit} className="space-y-16">
            {/* Section 1: Business Information */}
            <div className="space-y-10">
              <div className="flex items-center gap-3 border-l-4 border-black pl-4">
                <h2 className="text-2xl font-serif text-black">Business Information</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                <InputGroup
                  label="ENTERPRISE / SHOP NAME"
                  placeholder="Enter shop Name"
                  value={formData.shopName}
                  onChange={(val) => setFormData({ ...formData, shopName: val })}
                />
                <InputGroup
                  label="BUSINESS REGISTRATION ID (OPTIONAL)"
                  placeholder="e.g. REG-992011"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                <InputGroup
                  label="FIRST NAME"
                  placeholder="Shop Owner Name"
                  value={formData.ownerName.split(' ')[0] || ''}
                  onChange={(val) => setFormData({ ...formData, ownerName: `${val} ${formData.ownerName.split(' ').slice(1).join(' ')}` })}
                />
                <InputGroup
                  label="MIDDLE NAME"
                  placeholder="Optional"
                />
                <InputGroup
                  label="LAST NAME"
                  placeholder="Enter Shop Owner Last Name"
                  value={formData.ownerName.split(' ').slice(1).join(' ') || ''}
                  onChange={(val) => setFormData({ ...formData, ownerName: `${formData.ownerName.split(' ')[0] || ''} ${val}` })}
                />
              </div>
            </div>

            {/* Section 2: Access & Contact */}
            <div className="space-y-10">
              <div className="flex items-center gap-3 border-l-4 border-black pl-4">
                <h2 className="text-2xl font-serif text-black">Access & Contact</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                <InputGroup
                  label="BUSINESS EMAIL"
                  type="email"
                  placeholder="Shop Owner Email Id"
                  value={formData.email}
                  onChange={(val) => setFormData({ ...formData, email: val })}
                />
                <InputGroup
                  label="MASTER ACCESS PASSWORD"
                  type="password"
                  placeholder="••••••••••••"
                  value={formData.password}
                  onChange={(val) => setFormData({ ...formData, password: val })}
                  isPassword
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                <InputGroup
                  label="SUBSCRIPTION START DATE"
                  type="date"
                  value={formData.startDate}
                  onChange={(val) => setFormData({ ...formData, startDate: val })}
                />
                <InputGroup
                  label="SUBSCRIPTION END DATE"
                  type="date"
                  value={formData.endDate}
                  onChange={(val) => setFormData({ ...formData, endDate: val })}
                />
              </div>
            </div>

            {/* Section 3: Service Tier */}
            <div className="space-y-10">
              <div className="flex items-center gap-3 border-l-4 border-black pl-4">
                <h2 className="text-2xl font-serif text-black">Service Tier <span className="text-rose-500 text-lg">*</span></h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: p.name })}
                    className={`p-10 rounded-2xl border transition-all duration-300 flex flex-col gap-2 text-left ${formData.plan === p.name
                      ? 'bg-black text-[#d4af37] border-black shadow-xl'
                      : 'bg-white border-black/[0.05] text-[#ccc]'
                      }`}
                  >
                    <p className={`text-[10px] font-bold tracking-widest ${formData.plan === p.name ? 'text-[#d4af37]' : 'text-[#ccc]'}`}>{p.name}</p>
                    <p className={`text-xl font-bold ${formData.plan === p.name ? 'text-white' : 'text-black'}`}>{p.price}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Action */}
            <div className="pt-16 flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="luxury-button w-full sm:w-auto min-w-[320px] flex items-center justify-center gap-4"
              >
                {loading ? 'Processing...' : 'CREATE MERCHANT ACCOUNT'}
                <ChevronRight size={18} />
              </button>
            </div>
          </form>
        </div>

        <footer className="mt-12 text-center text-[10px] text-black/20 tracking-[0.4em] uppercase">
          © 2026 DIGI SAHYADRI SECURED INFRASTRUCTURE
        </footer>
      </div>
    </div>
  );
};

const InputGroup = ({ label, placeholder, value, onChange, type = "text", isPassword }) => {
  const [show, setShow] = useState(false);
  const isActuallyPassword = type === 'password';

  return (
    <div className="space-y-3">
      <label className="text-[11px] font-bold text-[#999] tracking-widest uppercase">{label}</label>
      <div className="relative">
        <input
          type={isActuallyPassword ? (show ? 'text' : 'password') : type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className={`w-full bg-black/[0.02] border border-black/[0.05] rounded-xl py-4 px-6 text-sm text-black font-semibold focus:outline-none focus:border-black/10 transition-all placeholder-black/20 ${isActuallyPassword ? 'pr-14' : ''}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(!show)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ccc] hover:text-black transition-all p-2"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateShopOwner;
